import '.ca{max-height:16rem;}.ssr-plugin'
import '.cb{aspect-ratio:2/1.4;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.at{position:relative;}.ssr-plugin'
import '.cc{max-height:27rem;}.ssr-plugin'
import '.cd{aspect-ratio:2/1;}.ssr-plugin'
import '.a{display:grid;}.ssr-plugin'
import '.ce{grid-template-columns:1fr 1fr;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.cf{grid-template-columns:1.5fr 1fr;}.ssr-plugin'
import '.cg{grid-template-rows:1fr 1fr;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { useTranslations } from '@i18n/useLanguage'
import Row from '@app/components/layout/Row'
import { Title } from '@app/components/core/typography/Title'
import { useState, useCallback } from 'react'
import { faImages } from '@fortawesome/pro-regular-svg-icons'
import { Button } from '@app/components/core/button/Button'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import useMediaQuery from '@app/util/useMediaQuery'
import { ImagePreview } from './blocks/ImagePreview'
import { ImageDisplay } from './blocks/ImageDisplay'
import { ImageOveriew } from './blocks/ImageOverview'
import { expandImage, StoredImage } from '@app/types/StoredImage'

export type PageMediaGalleryProps = {
  title: string
  images: (string | StoredImage)[]
  defaultImage?: string
  overviewRenderContainer?: HTMLElement
  style?: Style
}

export const PageMediaGallery = ({
  title,
  images = [],
  defaultImage,
  overviewRenderContainer,
  style,
}: PageMediaGalleryProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.mediaGallery
  const [displayedIndex, setDisplayedIndex] = useState<number>(null)
  const [overviewShown, setOverviewShown] = useState(false)

  const renderImagePreview = useCallback(
    (index: number, wrapperStyle?: Style) => {
      return (
        <ImagePreview
          key={index}
          image={images[index]}
          altFallback={title}
          index={index}
          wrapperStyle={wrapperStyle}
          imageStyle={{
            borderRadius: '1.25rem',
          }}
          onImageClick={setDisplayedIndex}
        />
      )
    },
    [images, title],
  )

  const modalHeader = useCallback(
    (onClose: () => void) => (
      <Row
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title.H4 style={{ margin: 0 }}>{title}</Title.H4>
        <CloseButton onClick={onClose} />
      </Row>
    ),
    [title],
  )

  const renderOveriew = useCallback(() => {
    return (
      <ImageOveriew
        images={images}
        altFallback={title}
        header={modalHeader(() => setOverviewShown(false))}
        onImageClick={setDisplayedIndex}
      />
    )
  }, [images, modalHeader, title])

  const goToOverview = useCallback(() => {
    setOverviewShown(true)
    setDisplayedIndex(null)
  }, [])

  const renderDisplay = useCallback(() => {
    const displayedImage = expandImage(images[displayedIndex], title)
    return (
      <ImageDisplay
        displayedImage={displayedImage.src}
        displayedImageAlt={displayedImage.altText}
        overviewButtonLabel={translations.allMedia}
        header={modalHeader(() => setDisplayedIndex(null))}
        canGoBack={displayedIndex !== 0}
        canGoForwad={displayedIndex !== images.length - 1}
        goBack={() => setDisplayedIndex((prev) => prev - 1)}
        goForward={() => setDisplayedIndex((prev) => prev + 1)}
        goToOverview={images.length > 1 ? goToOverview : undefined}
      />
    )
  }, [
    images,
    displayedIndex,
    title,
    translations.allMedia,
    modalHeader,
    goToOverview,
  ])

  const renderModal = useCallback(() => {
    if (displayedIndex === null && !overviewShown) {
      return null
    }
    return (
      <ModalWrapper
        renderContainer={overviewRenderContainer}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6.5px)',
          overflowY: 'scroll',
          alignItems: displayedIndex !== null ? 'center' : 'flex-start',
        }}
      >
        {displayedIndex !== null ? renderDisplay() : renderOveriew()}
      </ModalWrapper>
    )
  }, [
    displayedIndex,
    overviewShown,
    renderDisplay,
    renderOveriew,
    overviewRenderContainer,
  ])

  // Early return after all hooks
  if (images.length === 0 && !defaultImage) {
    return null
  }

  const renderDefaultImagePreview = () => {
    return (
      <ImagePreview
        image={defaultImage}
        index={0}
        imageStyle={{
          borderRadius: '1.25rem',
        }}
        onImageClick={() => {}}
      />
    )
  }

  if (isMobile) {
    if (images.length === 0) {
      return (
        <styled.div className="ca cb d at"
          style={{
            ...style,
          }}
        >
          {renderDefaultImagePreview()}
        </styled.div>
      )
    }

    const renderOverviewButton = images.length > 1

    return (
      <>
        {renderModal()}
        <styled.div className="ca cb d at"
          style={{
            ...style,
          }}
        >
          {renderImagePreview(0)}
          {renderOverviewButton && (
            <Button
              style={{
                backgroundColor: 'var(--palette-white)',
                position: 'absolute',
                right: '1rem',
                bottom: '1rem',
              }}
              variant="secondary"
              size="small"
              label={translations.allMedia}
              icon={faImages}
              iconPosition="left"
              onClick={() => setOverviewShown(true)}
            />
          )}
        </styled.div>
      </>
    )
  }

  if (images.length === 0) {
    return (
      <>
        <styled.div className="cc cd"
          data-testid="page-media-gallery"
          style={{ ...style }}
        >
          {renderDefaultImagePreview()}
        </styled.div>
      </>
    )
  }

  if (images.length === 1) {
    return (
      <>
        {renderModal()}
        <styled.div className="cc cd"
          data-testid="page-media-gallery"
          style={{ ...style }}
        >
          {renderImagePreview(0)}
        </styled.div>
      </>
    )
  }

  if (images.length === 2) {
    return (
      <>
        {renderModal()}
        <styled.div className="cc cd a ce g"
          data-testid="page-media-gallery"
          style={{
            ...style,
          }}
        >
          {renderImagePreview(0)}
          {renderImagePreview(1)}
        </styled.div>
      </>
    )
  }

  const renderOverviewButton = images.length > 1

  return (
    <>
      {renderModal()}
      <styled.div className="cc cd a cf cg g at"
        data-testid="page-media-gallery"
        style={{
          ...style,
        }}
      >
        {renderImagePreview(0, { gridColumn: '1/2', gridRow: '1/3' })}
        {images.length > 1 &&
          renderImagePreview(1, { gridColumn: '2/3', gridRow: '1/2' })}
        {images.length > 2 &&
          renderImagePreview(2, {
            gridColumn: '2/3',
            gridRow: '2/3',
          })}
        {images
          .slice(3)
          .map((_, index) =>
            renderImagePreview(index + 3, { display: 'none' }),
          )}
        {renderOverviewButton && (
          <Button
            style={{
              backgroundColor: 'var(--palette-white)',
              position: 'absolute',
              right: '1rem',
              bottom: '1rem',
            }}
            variant="secondary"
            size="small"
            label={translations.allMedia}
            icon={faImages}
            iconPosition="left"
            onClick={() => setOverviewShown(true)}
          />
        )}
      </styled.div>
    </>
  )
}
